// Application entry point for bundling approach with propshaft

// Turbo Drive와 Stimulus 사용
import "@hotwired/turbo-rails";
import "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

function toAbsoluteUrl(value) {
  if (!value) return null;

  try {
    return value instanceof URL ? value : new URL(String(value), window.location.origin);
  } catch (error) {
    console.warn('Unable to resolve URL value', { value, error });
    return null;
  }
}

function toUrlString(value) {
  if (!value) return '';
  return value instanceof URL ? value.toString() : String(value);
}

// Stimulus 컨트롤러 로드
import { application } from "./controllers/application";
import { buildLocalizedUrl, getCurrentLocale } from "./lib/locale";
window.Stimulus = application;
window.getCurrentLocale = getCurrentLocale;
window.buildLocalizedUrl = buildLocalizedUrl;

import "./controllers";

// Tailwind CSS는 빌드 타임에 컴파일되어 사용됩니다.
// import "tailwindcss";

// Turbo 설정 - Progress Bar 지연을 대폭 줄여서 빠른 응답성 확보
Turbo.config.drive.progressBarDelay = 50; // 기본값 500ms -> 50ms로 대폭 단축

// Turbo 이벤트 관리
document.addEventListener('turbo:load', () => {
  console.log('Turbo Drive loaded', { timestamp: new Date().toISOString() });
});

document.addEventListener('turbo:frame-load', (event) => {
  console.log('Turbo Frame loaded', {
    id: event.target.id,
    timestamp: new Date().toISOString()
  });
});

// Turbo Frame 에러 처리 추가
document.addEventListener('turbo:frame-missing', (event) => {
  console.error('Turbo Frame missing:', {
    frameId: event.detail.target,
    timestamp: new Date().toISOString()
  });
});

// Turbo Frame 렌더링 시작 이벤트
document.addEventListener('turbo:frame-render', (event) => {
  console.log('Turbo Frame render started:', {
    frameId: event.target.id,
    timestamp: new Date().toISOString()
  });
});

document.addEventListener('turbo:before-fetch-request', (event) => {
  const fetchOptions = event.detail.fetchOptions || (event.detail.fetchOptions = {});
  const originalUrlValue = event.detail.url ?? fetchOptions.url;

  if (!originalUrlValue) {
    console.warn('Turbo before-fetch-request: missing request URL, skipping localization');
    return;
  }

  const localizedUrlValue = buildLocalizedUrl(originalUrlValue);
  if (!localizedUrlValue) {
    return;
  }

  const originalUrl = toAbsoluteUrl(originalUrlValue);
  const localizedUrl = toAbsoluteUrl(localizedUrlValue);

  if (!localizedUrl) {
    return;
  }

  if (!originalUrl || localizedUrl.toString() !== originalUrl.toString()) {
    event.detail.url = localizedUrl;
    fetchOptions.url = localizedUrl.toString();
  } else if (!fetchOptions.url) {
    fetchOptions.url = localizedUrl.toString();
  }

  console.log('Turbo before fetch request', { 
    url: fetchOptions.url || toUrlString(event.detail.url),
    timestamp: new Date().toISOString() 
  });

  if (!fetchOptions.headers) {
    fetchOptions.headers = {};
  }

  // CSRF 토큰 관리 개선
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (csrfToken) {
    fetchOptions.headers['X-CSRF-Token'] = csrfToken;
    console.log('CSRF token added to request headers');
  } else {
    console.warn('CSRF token not found in meta tag');
  }
  
  // Content-Type 헤더 확실히 설정
  if (!fetchOptions.headers['Content-Type'] && 
      fetchOptions.method && fetchOptions.method !== 'GET') {
    fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
  }
  
  // 요청 시 기존 flash 메시지 정리
  cleanupFlashMessages();
});

document.addEventListener('turbo:before-fetch-response', (event) => {
  console.log('Turbo before fetch response', { 
    status: event.detail.fetchResponse.response.status,
    timestamp: new Date().toISOString() 
  });
  
  // CSRF 토큰 만료 감지 및 처리
  if (event.detail.fetchResponse.response.status === 422) {
    console.warn('Possible CSRF token issue (422 status)');
    refreshCSRFToken();
  }
});

// CSRF 토큰 새로고침 후 페이지 재로드 처리
document.addEventListener('turbo:before-stream-render', (event) => {
  // Stream 렌더링 전 CSRF 토큰 확인
  const currentToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (!currentToken) {
    console.warn('CSRF token missing before stream render');
  }

  // Turbo Stream 렌더링 디버깅
  console.log('Turbo Stream rendering:', {
    streamAction: event.detail?.newStream?.action,
    target: event.detail?.newStream?.target,
    timestamp: new Date().toISOString()
  });
});

// 렌더링 오류 처리
document.addEventListener('turbo:render', (event) => {
  console.log('Turbo render completed');
});

// Turbo 오류 처리
document.addEventListener('turbo:fetch-request-error', (event) => {
  console.error('Turbo fetch request error:', event.detail);
  
  // 네트워크 오류 시 사용자에게 알림
  const msg = document.body?.dataset?.networkError || '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
  showGlobalErrorMessage(msg);
});

// Turbo 방문 시작 시 전역 상태 정리
document.addEventListener('turbo:visit', (event) => {
  console.log('Turbo visit started to:', event.detail.url);
  
  // 모든 드롭다운 닫기
  closeAllDropdowns();
  
  // 모든 모달 닫기
  closeAllModals();
  
  // 사이드바 닫기
  closeSidebar();
});

// 페이지 캐시 전 전역 정리
document.addEventListener('turbo:before-cache', (event) => {
  console.log('Global cleanup before cache');
  
  // 전역 상태 정리
  cleanupGlobalState();
});

// 방문 실패 시 처리
document.addEventListener('turbo:visit-failed', (event) => {
  console.error('Turbo visit failed:', event.detail);
  
  // 사용자에게 오류 알림
  const msg = document.body?.dataset?.pageLoadError || '페이지를 불러오는 중 오류가 발생했습니다.';
  showGlobalErrorMessage(msg);
});

// Flash 메시지 정리
function cleanupFlashMessages() {
  const flashMessages = document.querySelectorAll('[data-turbo-temporary]');
  flashMessages.forEach(message => {
    if (message.dataset.flashMessage) {
      message.remove();
    }
  });
}

// CSRF 토큰 새로고침
async function refreshCSRFToken() {
  try {
    console.log('Refreshing CSRF token...');
    
    const response = await fetch('/rails/info/routes', {
      method: 'HEAD',
      headers: {
        'Accept': 'text/html'
      }
    });
    
    if (response.ok) {
      // 응답 헤더에서 새 CSRF 토큰 추출 (Rails 7+ 방식)
      const newToken = response.headers.get('X-CSRF-Token');
      
      if (newToken) {
        // 기존 meta 태그 업데이트
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        if (metaTag) {
          metaTag.content = newToken;
          console.log('CSRF token refreshed successfully');
        }
        
        // 모든 form의 hidden CSRF 필드 업데이트
        const csrfInputs = document.querySelectorAll('input[name="authenticity_token"]');
        csrfInputs.forEach(input => {
          input.value = newToken;
        });
        
        return newToken;
      } else {
        console.warn('New CSRF token not found in response headers');
      }
    }
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
  }
  
  return null;
}

// 폼 제출 전 CSRF 토큰 검증
function validateCSRFToken(form) {
  const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const formToken = form.querySelector('input[name="authenticity_token"]')?.value;
  
  if (!metaToken) {
    console.error('CSRF meta token missing');
    return false;
  }
  
  if (!formToken) {
    console.warn('Form CSRF token missing, adding from meta tag');
    // meta 태그에서 토큰 복사
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'authenticity_token';
    tokenInput.value = metaToken;
    form.appendChild(tokenInput);
    return true;
  }
  
  if (metaToken !== formToken) {
    console.warn('CSRF token mismatch, updating form token');
    form.querySelector('input[name="authenticity_token"]').value = metaToken;
  }
  
  return true;
}

// 폼 제출 이벤트 리스너 (CSRF 토큰 검증)
document.addEventListener('turbo:submit-start', (event) => {
  const form = event.target;
  if (form.tagName === 'FORM') {
    validateCSRFToken(form);
  }
});

// 전역 상태 관리 함수들
function closeAllDropdowns() {
  const dropdowns = document.querySelectorAll('[data-controller*="dropdown"]');
  dropdowns.forEach(dropdown => {
    const controller = window.Stimulus.getControllerForElementAndIdentifier(dropdown, "dropdown");
    if (controller && controller.openValue) {
      controller.forceClose();
    }
  });
}

function closeAllModals() {
  const modals = document.querySelectorAll('[data-controller*="modal"]');
  modals.forEach(modal => {
    const controller = window.Stimulus.getControllerForElementAndIdentifier(modal, "modal");
    if (controller && controller.isOpen) {
      controller.close();
    }
  });
}

function closeSidebar() {
  const sidebar = document.querySelector('[data-controller*="sidebar"]');
  if (sidebar) {
    const controller = window.Stimulus.getControllerForElementAndIdentifier(sidebar, "sidebar");
    if (controller && controller.openValue) {
      controller.closeImmediately();
    }
  }
}

function cleanupGlobalState() {
  // body 스크롤 복원
  document.body.style.overflow = '';
  
  // 모든 애니메이션 타이머 정리
  const activeTimers = document.querySelectorAll('[data-animation-timer]');
  activeTimers.forEach(element => {
    const timerId = element.dataset.animationTimer;
    if (timerId) {
      clearTimeout(parseInt(timerId));
      element.removeAttribute('data-animation-timer');
    }
  });
  
  // 모든 UI 컴포넌트 상태 정리
  closeAllDropdowns();
  closeAllModals();
  closeSidebar();
  
  console.log('Global state cleanup completed');
}

function showGlobalErrorMessage(message) {
  // 기존 오류 메시지가 있으면 제거
  const existingError = document.querySelector('.global-error-message');
  if (existingError) {
    existingError.remove();
  }
  
  // 새 오류 메시지 생성
  const errorDiv = document.createElement('div');
  errorDiv.className = 'global-error-message fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
  errorDiv.textContent = message;
  
  // DOM에 추가
  document.body.appendChild(errorDiv);
  
  // 5초 후 자동 제거
  setTimeout(() => {
    if (errorDiv.parentNode) {
      errorDiv.remove();
    }
  }, 5000);
}
